<template>
	<div :id="id" class="Observation" :class="classes">
		<!-- species heading-->
		<div class="Observation-species">
			<h3 class="Heading Heading--h4" :class="species.classes">
				<a :href="species.href">
					<span class="Heading-main">{{ species.commonName }}</span>
					<span class="Heading-sub Heading-sub--inline Heading-sub--sci">{{ species.sciName }}</span>
				</a>
			</h3>
		</div>
		<!-- number observed-->
		<div class="Observation-numberObserved">
			<span>
				<span class="is-visuallyHidden">Number observed:</span>
				{{ numberObserved }}
			</span>
		</div>
		<!-- tags-->
		<div v-if="tags" class="Observation-tags">
			<span class="is-visuallyHidden">Tags:</span>
			<div v-for="(tag, index) in tags" :key="index">
				<span :title="tag.title" class="Button Button--pill Button--tiny Button--alert">{{ tag.label }}</span>
			</div>
		</div>
		<!-- comments -->
		<div v-if="comments" class="Observation-comments">
			<p>
				<span class="is-visuallyHidden">Comments:</span>
				{{ comments }}
			</p>
		</div>
		<!-- observation otherMeta-->
		<div v-if="meta" class="Observation-meta">
			<div v-for="(meta, index) in meta" class="Observation-meta-item" :key="index">
				<span class="Observation-meta-item-label">{{ meta.label }}:</span>
				<span class="observation-meta-item-value">{{ meta.value }}</span>
			</div>
		</div>
		<!-- media-->
		<div class="Observation-media">
			<!-- @slot This slot holds the media -->
			<slot />
		</div>
		<!-- tools -->
		<div class="Observation-tools">
			<!--did not add review because reviewer is in userRoles-->
			<a
				v-for="(tool, index) in tools"
				:key="index"
				:href="tool.href"
				class="Observation-tools-item Observation-tools-item--hideLabelForSmall"
				:title="tool.title"
			>
				<CloIcon :symbol="tool.icon" />
				<span class="Observation-tools-item-label">{{ tool.label }}</span>
			</a>
		</div>
	</div>
</template>

<script>
import CloButton from '@/base/CloButton/CloButton.vue';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
export default {
	components: { CloButton, CloHeading, CloIcon },
	data() {
		return {};
	},
	props: {
		/**
		 * This sets the flush property of the component
		 */
		flush: {
			type: Boolean,
			default: false,
		},
		/**
		 * This sets the loose property of the component
		 */
		loose: {
			type: Boolean,
			default: false,
		},
		/**
		 * This sets the reverse property of the component
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * When set to true, this adds a dark border around the observation component.
		 */
		reverseDarkerBorder: {
			type: Boolean,
			dedault: false,
		},
		/**
		 * This sets the id of the component
		 */
		id: {
			type: String,
			default: '',
		},
		/**
		 * This contains the species of the observation being added
		 */
		species: {
			type: Object,
			default() {
				return {};
			},
		},
		/**
		 * This sets the number of observed species property to component
		 */
		numberObserved: {
			type: String,
			default: '',
		},
		tags: Array,
		/**
		 * This adds comments to the component
		 */
		comments: {
			type: String,
			default: '',
		},
		/**
		 * This sets the tools(like edit, remove, review) availabble for each posted observation.
		 */
		tools: {
			type: Array,
			default() {
				return [];
			},
		},
		/**
		 * This sets the meta data available for an observation.
		 */
		meta: {
			type: Array,
			default() {
				return [];
			},
		},
	},

	computed: {
		classes: function () {
			let classes = [];
			let prefix = 'Observation--';
			if (this.flush) {
				classes.push(prefix + 'flushInPageSection');
			}
			if (this.loose) {
				classes.push(prefix + 'loose');
			}
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			if (this.reverseDarkerBorder) {
				classes.push(prefix + 'reverseDarkBorder');
			}
			return classes.join(' ');
		},
	},
};
</script>

<style lang="scss">
.Observation {
	list-style: none;
	margin: 0;
	@include text(2);
	color: $color-text;
	border-top: 1px solid $color-neutral-1;
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	@include inset-sm;
	@include breakpoint(medium) {
		@include inset-squish-md;
	}
	&--flushInPageSection {
		margin-left: (-$space-md);
		margin-right: (-$space-md);
		@include breakpoint(large) {
			margin-left: (-$space-lg);
			margin-right: (-$space-lg);
		}
	}
	&[hidden] {
		display: none !important;
	}
	@supports (display: grid) {
		display: grid;
		grid-template-columns: auto auto 1fr auto;
		// grid-auto-flow: column;
		grid-column-gap: $space-md;
		grid-row-gap: $space-xs;
	}
}

.Observation-numberObserved {
	//@include h5;
	@include inline-sm;
	min-width: 2em;
	text-align: right;
	[dir='rtl'] & {
		text-align: left;
	}
	order: -2;
	@supports (display: grid) {
		grid-column: 1 / 2;
		grid-row: 1 / 2;
		margin: 0;
	}
}

.Observation-species {
	@include inline-sm;
	order: -1;
	@supports (display: grid) {
		grid-column: 2 / 3;
		grid-row: 1 / 2;
		margin: 0;
		.Heading {
			margin-bottom: 0;
		}
	}
}

.Observation-tags {
	display: flex;
	flex-wrap: wrap;
	.Button,
	.Badge {
		&:not(:last-child) {
			margin-right: 0.5em;
			[dir='rtl'] & {
				margin-right: 0;
				margin-left: 0.5em;
			}
		}
	}
	@supports (display: grid) {
		grid-column: 3 / 4;
		grid-row: 1 / 2;
	}
}

.Observation-tools {
	display: flex;
	align-self: center;
	justify-content: flex-end;
	flex-grow: 1;
	@supports (display: grid) {
		grid-column: 4 / 5;
		grid-row: 1 / 2;
	}
	&--complex {
		justify-content: flex-start;
		flex-wrap: wrap;
		@include breakpoint(medium) {
			justify-content: flex-end;
		}
		@supports (display: grid) {
			grid-column: 2 / -1;
			grid-row: auto;
			@include breakpoint(medium) {
				grid-column: 4 / 5;
				grid-row: 1 / 2;
			}
		}
		.Observation-tools-item {
			padding-top: $space-xs;
			padding-bottom: $space-xs;
		}
	}

	.Observation-tools-item {
		display: flex;
		align-items: baseline;
		padding-right: $space-md;
		&:last-child {
			padding-right: 0;
		}
		[dir='rtl'] & {
			padding-left: $space-md;
			padding-right: 0;
			&:last-child {
				padding-left: 0;
			}
		}
		.Icon {
			@include icon-sm;
			align-self: center;
			@include inline-xs;
			&:last-child {
				margin: 0;
			}
		}
		.Observation-tools-item-label {
			@include text(2);
			&--bold {
				font-weight: bold;
			}
		}
		&--hideLabelForSmall {
			@include breakpoint(medium, down) {
				.Icon {
					margin: 0;
				}
				.Observation-tools-item-label {
					@include visuallyHidden;
				}
			}
		}
	}

	// itemGroup is an optional wrapper around items
	.Observation-tools-itemGroup {
		display: flex;
		align-self: center;
		@include inline-lg;
		&:last-child {
			margin: 0;
		}
	}
}

.Observation-comments {
	flex-basis: 100%;
	order: 1;
	margin-left: $space-lg;
	[dir='rtl'] & {
		margin-left: 0;
		margin-right: $space-lg;
	}
	margin-bottom: $space-xs;
	p {
		@include constrainBody;
		@include text(2);
		@include stack-xs;
	}
	@supports (display: grid) {
		grid-column: 2 / -1;
		margin: 0;
	}
}

.Observation-meta {
	flex-basis: 100%;
	order: 1;
	margin-left: $space-lg;
	[dir='rtl'] & {
		margin-left: 0;
		margin-right: $space-lg;
	}
	margin-bottom: $space-xs;
	.Observation-meta-item {
		&:not(last-child) {
			@include stack-sm;
		}
	}
	.Observation-meta-item-label {
		@include text(1);
		text-transform: uppercase;
		color: $color-text-muted;
	}
	.Observation-meta-item-value {
		font-weight: bold;
		@include text(2);
	}
	.Observation-meta-item-table {
		@include text(2);
	}
	@supports (display: grid) {
		grid-column: 2 / -1;
		margin: 0;
	}
}

.Observation-media {
	@include stack-md;
	margin-top: $space-xs;
	display: flex;
	flex-wrap: wrap;
	flex-basis: 100%;
	order: 1;
	margin-left: $space-lg;
	[dir='rtl'] & {
		margin-left: 0;
		margin-right: $space-lg;
	}
	margin-bottom: $space-xs;
	@supports (display: grid) {
		grid-column: 2 / -1;
		margin: 0;
		margin-top: $space-xs;
		@include breakpoint(medium) {
			grid-column: 2 / -1;
		}
		&--fill {
			grid-column: 1 / -1;
		}
		&--sm-fill {
			grid-column: 1 / -1;
			@include breakpoint(medium) {
				grid-column: 2 / -1;
			}
		}
	}
}

.Observation-media-item {
	margin: 0 1px 1px 0;
	[dir='rtl'] & {
		margin: 0 0 1px 1px;
	}
	img {
		height: 90px;
		width: auto;
		max-width: 100%;
		@include breakpoint(medium) {
			height: 120px;
		}
	}
}

/* ----------------
	Variants
---------------- */

// Reverse variant - for dark background
.Observation--reverse {
	color: $color-text-reverse;
	border-top: 1px solid $color-neutral-4;
	.Observation-meta-item-label {
		color: $color-text-muted-reverse;
	}
	.Observation-tools-item {
		color: $color-link-reverse;
		&:hover,
		&:focus,
		&:active {
			color: $color-link-hover-reverse;
		}
		label {
			color: inherit;
		}
	}
}

// Variant of reverse with darker borders
.Observation--reverseDarkBorder {
	// change to $color-neutral-6 when available
	border-top: 1px solid #303030;
}

// Loose variant - more space around and between
.Observation--loose {
	@include breakpoint(medium) {
		@include inset-md;
	}
	@supports (display: grid) {
		grid-row-gap: $space-sm;
	}
}
</style>